/**
 * The backoffice reconciler. Every read and call is keyed on `staffUid`: a
 * change of scope (sign-out, another account, a lost staff grant) returns each
 * list to `loading` and every act to idle in the same step, and switchMap
 * unsubscribes the previous scope's listeners and callables, so a late
 * answer for it never lands.
 */
import { failurePermanence, firebaseStatusCode } from "@mhmo91/rx";
import { session } from "@momo/views";
import { ConnectClient, SignLicence } from "@revia/types";
import {
	catchError,
	defer,
	distinctUntilChanged,
	EMPTY,
	filter,
	from,
	map,
	merge,
	mergeMap,
	of,
	startWith,
	switchMap,
	tap,
} from "rxjs";
import { z } from "zod";
import { staffNotice, staffUid } from "../staff/staff.state.ts";
import {
	accessRequests$,
	approveAccessRequest,
	connectClient,
	gitHubConnection,
	landingViews$,
	licences$,
	setAccessRequestStatus,
	signLicence,
} from "./backoffice.api.ts";
import {
	accessRequests,
	type ConnectAct,
	chooseVisitRange$,
	closeConnect$,
	connectAct,
	connectClient$,
	gitHub,
	recheckGitHub$,
	type SignRefusal,
	chooseRequestsFilter$,
	closeSign$,
	type Decision,
	decide$,
	decisions,
	landingViews,
	licences,
	orgNames,
	reloadLists$,
	requestsFilter,
	type SignAct,
	signAct,
	signLicence$,
	visitSpan,
	visitRange,
} from "./backoffice.state.ts";

/** The signing refusal a server reason names: a passed end, a name that
 *  gives no environment, or Revia's signing not set up. */
const signRefusal = (reason: SignLicence.SignLicenceRefusal): SignRefusal =>
	reason === "ends-at-not-future" ? "ended" : reason === "client-name-unusable" ? "unusable-name" : "not-set-up";

// staffUid reads the session and the staff notice, so either may move it.
merge(session.$, staffNotice.$).pipe(
	map(() => staffUid.get()),
	distinctUntilChanged(),
	tap(() => {
		decisions.replace({});
		orgNames.replace({});
		signAct.replace({ status: "idle" });
		connectAct.replace({ status: "idle" });
	}),
	switchMap((uid) =>
		merge(
			reloadLists$.pipe(
				startWith(undefined),
				tap(() => {
					accessRequests.replace({ status: "loading" });
					licences.replace({ status: "loading" });
				}),
				switchMap(() =>
					uid === null
						? EMPTY
						: merge(
								accessRequests$().pipe(
									map((items) => ({ status: "ready" as const, items })),
									catchError((err: unknown) => {
										console.error("[backoffice] access requests failed", err);
										return of({ status: "error" as const });
									}),
									tap((read) => accessRequests.replace(read)),
								),
								licences$().pipe(
									map((items) => ({ status: "ready" as const, items })),
									catchError((err: unknown) => {
										console.error("[backoffice] licences failed", err);
										return of({ status: "error" as const });
									}),
									tap((read) => licences.replace(read)),
								),
							),
				),
			),
			// Whether the App can act on Revent, asked on the scope and on request.
			merge(reloadLists$, recheckGitHub$).pipe(
				startWith(undefined),
				tap(() => gitHub.replace({ status: "loading" })),
				switchMap(() =>
					uid === null
						? EMPTY
						: defer(() => gitHubConnection()).pipe(
								map((connection) => ({ status: "ready" as const, connection })),
								catchError((err: unknown) => {
									console.error("[backoffice] GitHub connection failed", err);
									return of({ status: "error" as const });
								}),
							),
				),
				tap((read) => gitHub.replace(read)),
			),
			// The visit report, keyed on its span as on the scope: either change
			// returns it to unanswered and drops the earlier read's answer.
			merge(visitRange.$, reloadLists$).pipe(
				map(() => visitSpan(visitRange.value, Date.now())),
				tap(() => landingViews.replace({ status: "loading" })),
				switchMap((span) =>
					uid === null
						? EMPTY
						: defer(() => landingViews$(span.from, span.to)).pipe(
								map((items) => ({ status: "ready" as const, ...span, items })),
								catchError((err: unknown) => {
									console.error("[backoffice] visit report failed", err);
									return of({ status: "error" as const });
								}),
							),
				),
				tap((read) => landingViews.replace(read)),
			),
			uid === null
				? EMPTY
				: merge(
						chooseVisitRange$.pipe(
							filter((next) => next !== visitRange.value),
							tap((next) => visitRange.set(next)),
						),
						// Changing the status shown lets go of the requests decided here,
						// except one still in flight.
						chooseRequestsFilter$.pipe(
							filter((next) => next !== requestsFilter.value),
							tap((next) => {
								requestsFilter.set(next);
								decisions.replace(
									Object.fromEntries(
										Object.entries(decisions.value).filter(([, d]) => d.state === "busy"),
									),
								);
							}),
						),
						decide$.pipe(
							// Synchronous with the press: a second press on a request in
							// flight finds it already busy.
							filter(({ id }) => decisions.value[id]?.state !== "busy"),
							tap(({ id, act }) =>
								decisions.replace({ ...decisions.value, [id]: { act, state: "busy" } }),
							),
							// mergeMap: each request is decided independently.
							mergeMap((press) => {
								const { id, act } = press;
								return from(
									press.act === "granted"
										? approveAccessRequest({ requestId: id, name: press.name })
										: setAccessRequestStatus(id, press.act),
								).pipe(
									map((): Decision => ({ act, state: "done" })),
									catchError((err: unknown) => {
										console.error("[backoffice] decision failed", id, act, err);
										const code = firebaseStatusCode(err);
										return of<Decision>({
											act,
											state:
												code === "permission-denied"
													? "denied"
													: code === "not-found"
														? "missing"
														: code === "failed-precondition"
															? "settled"
															: "retry",
										});
									}),
									tap((decision) =>
										decisions.replace({ ...decisions.value, [id]: decision }),
									),
								);
							}),
						),
						// A press while one is in flight finds the act already busy;
						// closing the form cancels what it asked and lets go of its answer.
						merge(
							signLicence$.pipe(
								filter(() => signAct.value.status !== "busy"),
								tap(() => signAct.replace({ status: "busy" })),
							),
							closeSign$.pipe(map(() => null)),
						).pipe(
							switchMap((input) =>
								input === null
									? of<SignAct>({ status: "idle" })
									: from(signLicence(input)).pipe(
											map((out): SignAct => ({ status: "signed", token: out.token, environment: out.environment })),
											catchError((err: unknown) => {
												const reason = z
													.object({ details: z.object({ reason: SignLicence.SignLicenceRefusal }) })
													.safeParse(err).data?.details.reason;
												const act: SignAct =
													firebaseStatusCode(err) === "permission-denied"
														? { status: "refused", why: "denied" }
														: reason === undefined
															? { status: "failed", retryable: failurePermanence(err) !== "permanent" }
															: { status: "refused", why: signRefusal(reason) };
												// Signing not set up is Revia's fault to fix, so it is logged too.
												if (act.status === "failed" || (act.status === "refused" && act.why === "not-set-up"))
													console.error("[backoffice] sign licence failed", err);
												return of(act);
											}),
										),
							),
							tap((act) => signAct.replace(act)),
						),
						merge(
							connectClient$.pipe(
								filter(() => connectAct.value.status !== "busy"),
								tap(() => connectAct.replace({ status: "busy" })),
							),
							closeConnect$.pipe(map(() => null)),
						).pipe(
							switchMap((input) =>
								input === null
									? of<ConnectAct>({ status: "idle" })
									: from(connectClient(input)).pipe(
											map((out): ConnectAct => ({ status: "answered", out })),
											catchError((err: unknown) => {
												const reason = z
													.object({ details: z.object({ reason: ConnectClient.ConnectClientRefusal }) })
													.safeParse(err).data?.details.reason;
												const act: ConnectAct =
													firebaseStatusCode(err) === "permission-denied"
														? { status: "refused", why: "denied" }
														: reason === undefined
															? { status: "failed", retryable: failurePermanence(err) !== "permanent" }
															: reason === "github-not-connected"
																? { status: "refused", why: "not-connected" }
																: reason === "github-not-installed"
																	? { status: "refused", why: "not-installed" }
																	: { status: "refused", why: signRefusal(reason) };
												if (act.status === "failed" || (act.status === "refused" && act.why === "not-set-up"))
													console.error("[backoffice] connect client failed", err);
												return of(act);
											}),
										),
							),
							tap((act) => connectAct.replace(act)),
						),
					),
		),
	),
).subscribe();
